import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SLIDES } from "@/mocks/comics";
import { formatViews } from "@/lib/comics";
import HeroSlideCard from "@/pages/home/components/HeroSlideCard";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [keyword, setKeyword] = useState("");
  const slide = SLIDES[index];

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const go = (next: number) => setIndex((next + SLIDES.length) % SLIDES.length);

  const onSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          key={slide.id}
          src={slide.cover}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-top opacity-45 animate-fade-in blur-[1px] scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/85 to-background-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/60" />
      </div>

      <div className="relative w-full px-4 md:px-6 pt-10 md:pt-16 pb-10 md:pb-16">
        <div className="flex items-center gap-2 text-[11px] font-label tracking-[0.24em] text-accent-400 mb-5">
          <span className="w-8 h-px bg-accent-500" />
          MANGA HAVEN · 2026 EDITION
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-8 lg:gap-12 items-start">
          <div className="min-w-0">
            <h1 className="font-heading font-bold text-3xl md:text-[44px] leading-[1.15] tracking-tight text-foreground-50 max-w-[720px]">
              禁漫天堂 · <span className="text-gradient">最新动漫漫画资源</span>下载平台
            </h1>
            <p className="mt-4 text-sm md:text-[15px] leading-7 text-foreground-300 max-w-[620px]">
              覆盖热血、恋爱、奇幻、悬疑等 8 大题材的漫画作品库，按状态、地区、年份多维筛选，支持全文关键词检索与章节索引，快速定位你想追的那一本。
            </p>

            <form onSubmit={onSearch} className="mt-6 flex flex-col sm:flex-row items-stretch gap-2 max-w-[560px]">
              <div className="relative flex-1">
                <i className="ri-search-2-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="输入漫画名称、作者或关键词，例如「机甲」"
                  aria-label="搜索漫画关键词"
                  className="w-full h-11 md:h-12 pl-11 pr-4 rounded-md bg-background-900/80 border border-background-700/70 text-sm text-foreground-100 placeholder:text-foreground-500 outline-none focus:border-accent-400/80 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="h-11 md:h-12 px-6 rounded-md bg-accent-500 text-background-950 text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-compass-3-line" />
                立即搜索
              </button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-500 mr-1">热门搜索</span>
              {["机甲", "悬疑推理", "恋爱", "国漫完结", "治愈日常"].map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="h-7 px-2.5 rounded-full text-[11px] bg-background-900/70 border hairline text-foreground-300 hover:text-accent-300 hover:border-accent-500/50 transition-colors whitespace-nowrap"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <dl className="mt-8 grid grid-cols-3 max-w-[520px] rounded-lg border hairline bg-background-900/60 backdrop-blur-sm divide-x divide-background-700/60 overflow-hidden">
              {[
                { label: "收录作品", value: "18+" },
                { label: "章节资源", value: "1,800+" },
                { label: "日均访问", value: "6.4万" },
              ].map((stat) => (
                <div key={stat.label} className="px-3 md:px-4 py-3">
                  <dd className="font-heading text-lg md:text-xl font-semibold text-accent-300">{stat.value}</dd>
                  <dt className="text-[11px] text-foreground-500 mt-1">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          <div className="rounded-lg border hairline bg-background-900/75 backdrop-blur-md overflow-hidden">
            <Link to={`/detail/${slide.id}`} className="block relative group">
              <div className="relative w-full h-[220px] md:h-[260px] overflow-hidden">
                <img
                  key={slide.id}
                  src={slide.cover}
                  alt={`《${slide.title}》焦点封面`}
                  title={`${slide.title} - 禁漫天堂焦点推荐`}
                  className="w-full h-full object-cover object-top animate-fade-in transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-900 via-background-900/20 to-transparent" />
                <span className="absolute top-3 left-3 h-6 px-2 rounded-md bg-accent-500 text-background-950 text-[11px] font-label flex items-center">
                  焦点推荐
                </span>
                <span className="absolute top-3 right-3 h-6 px-2 rounded-md bg-background-950/75 text-foreground-100 text-[11px] font-label flex items-center border border-background-700/70">
                  {slide.status}
                </span>
              </div>
            </Link>
            <div className="px-4 pb-4 -mt-2 relative">
              <h2 className="font-heading text-lg font-semibold text-foreground-50 truncate">
                <Link to={`/detail/${slide.id}`} className="hover:text-accent-300 transition-colors">
                  {slide.title}
                </Link>
              </h2>
              <p className="mt-1.5 text-xs leading-5 text-foreground-400 line-clamp-2">{slide.tagline}</p>
              <div className="mt-3 flex items-center justify-between text-[11px] text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line text-secondary-300" /> {slide.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-accent-300" /> {formatViews(slide.views)}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Link
                  to={`/detail/${slide.id}`}
                  className="flex-1 h-9 rounded-md bg-primary-500 text-background-50 text-xs flex items-center justify-center gap-1.5 hover:bg-primary-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-book-open-line" />
                  查看详情
                </Link>
                <Link
                  to={`/category/${SLIDES[index].categories.includes("恋爱") ? "romance" : "hot-blood"}`}
                  className="h-9 px-3 rounded-md border hairline text-xs text-foreground-200 flex items-center gap-1.5 hover:border-accent-500/50 hover:text-accent-300 transition-colors whitespace-nowrap"
                >
                  同类作品
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 pb-4 overflow-x-auto">
              {SLIDES.map((item, i) => (
                <HeroSlideCard key={item.id} comic={item} active={i === index} onSelect={() => setIndex(i)} />
              ))}
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 border-t hairline">
              <span className="text-[11px] text-foreground-500 font-label">
                {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="上一张"
                  onClick={() => go(index - 1)}
                  className="w-7 h-7 rounded-md border hairline text-foreground-300 flex items-center justify-center hover:text-accent-300 hover:border-accent-500/50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line" />
                </button>
                <button
                  type="button"
                  aria-label="下一张"
                  onClick={() => go(index + 1)}
                  className="w-7 h-7 rounded-md border hairline text-foreground-300 flex items-center justify-center hover:text-accent-300 hover:border-accent-500/50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}